/**
 * Notices of the technicians on one aircraft ("Hinweise der Technik", owner,
 * 17.10.2026): what a pilot should know before the flight that is not a
 * damage — „Autopilot schlingert, Fehlersuche läuft“, „Kurskreisel INOP“.
 * Editors and admins write and resolve them, every member reads them and
 * confirms „gelesen“. No push: they are shown where a pilot looks anyway —
 * on top of the aircraft's page and as a mark on the fleet overview.
 *
 * Three levels. `info` and `einschraenkung` say in so many words that the
 * aircraft stays airworthy. `gesperrt` is the safety-relevant one, „Flugzeug
 * gesperrt“, and exists only in a club whose admin switched it on (setting
 * `aircraft_grounding`, default off). Außencheck only SHOWS a grounding; the
 * booking is blocked where bookings live (Vereinsflieger), by people.
 * Reporting a damage on a grounded aircraft stays possible — a newly found
 * damage must never be lost — the report sheet only warns.
 *
 * One set of rules for API, demo store, web and app, like participation.ts.
 */
import { type MemberRole, hasAtLeastRole } from './status';

export const NOTICE_LEVELS = ['info', 'einschraenkung', 'gesperrt'] as const;
export type NoticeLevel = (typeof NOTICE_LEVELS)[number];

export function isNoticeLevel(value: unknown): value is NoticeLevel {
  return typeof value === 'string' && (NOTICE_LEVELS as readonly string[]).includes(value);
}

/** Higher is more urgent: the order on the aircraft's page and the colour of the fleet mark. */
export const NOTICE_LEVEL_RANK: Readonly<Record<NoticeLevel, number>> = {
  info: 1,
  einschraenkung: 2,
  gesperrt: 3,
};

/** A club without the setting cannot ground an aircraft (owner: „im Vereinsprofil … eine Einstellung“). */
export const DEFAULT_AIRCRAFT_GROUNDING = false;

/** Column widths of dbo.aircraft_notices (0022) and the form limits. */
export const NOTICE_TITLE_MIN = 3;
export const NOTICE_TITLE_MAX = 120;
export const NOTICE_BODY_MAX = 2000;

/** Who may write, change and resolve a notice: Technik and admins. */
export function mayManageNotices(role: MemberRole): boolean {
  return hasAtLeastRole(role, 'editor');
}

/** `gesperrt` only where the club switched grounding on; the other two always. */
export function noticeLevelAllowed(level: NoticeLevel, groundingEnabled: boolean | null | undefined): boolean {
  return level !== 'gesperrt' || groundingEnabled === true;
}

/** The levels a form offers in this club. */
export function noticeLevelsFor(groundingEnabled: boolean | null | undefined): readonly NoticeLevel[] {
  return NOTICE_LEVELS.filter((l) => noticeLevelAllowed(l, groundingEnabled));
}

/** The fields the rules below need; the DTO has more. */
export interface NoticeLike {
  readonly level: NoticeLevel;
  readonly createdAt: string;
  readonly resolvedAt: string | null;
  /** Last day (Europe/Berlin, YYYY-MM-DD) the notice is shown, inclusive; null = until resolved. */
  readonly validUntil: string | null;
}

/**
 * Shown right now: not resolved, and not past its optional last day. `today`
 * is the Berlin calendar day (`berlinDay`), so a notice „gilt bis 18.10.“
 * disappears at midnight in Germany, not at midnight UTC.
 */
export function isNoticeActive(notice: NoticeLike, today: string): boolean {
  return notice.resolvedAt === null && (notice.validUntil === null || notice.validUntil >= today);
}

/** Most urgent first, then newest first. */
export function compareNotices(a: NoticeLike, b: NoticeLike): number {
  const rank = NOTICE_LEVEL_RANK[b.level] - NOTICE_LEVEL_RANK[a.level];
  if (rank !== 0) return rank;
  return a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0;
}

/** The active notices of a list, in display order. */
export function activeNotices<N extends NoticeLike>(notices: readonly N[], today: string): N[] {
  return notices.filter((n) => isNoticeActive(n, today)).sort(compareNotices);
}

/** The most urgent level among the active ones, or null when there is none. */
export function highestNoticeLevel(notices: readonly NoticeLike[], today: string): NoticeLevel | null {
  let best: NoticeLevel | null = null;
  for (const n of notices) {
    if (!isNoticeActive(n, today)) continue;
    if (best === null || NOTICE_LEVEL_RANK[n.level] > NOTICE_LEVEL_RANK[best]) best = n.level;
  }
  return best;
}

/** True when an active `gesperrt` notice exists: the aircraft is shown as grounded everywhere. */
export function isGrounded(notices: readonly NoticeLike[], today: string): boolean {
  return highestNoticeLevel(notices, today) === 'gesperrt';
}

/**
 * Whether a „gelesen“ still counts. A change of what a pilot reads — level,
 * title, text — moves `revisedAt`, and an acknowledgement older than that
 * asks again: somebody who confirmed „Fehlersuche läuft“ has not read
 * „gesperrt“. A new last day or a resolution does not move it.
 */
export function isAcknowledgementCurrent(acknowledgedAt: string | null, revisedAt: string): boolean {
  return acknowledgedAt !== null && acknowledgedAt >= revisedAt;
}

/** `YYYY-MM-DD` that is a real calendar day. */
export function isNoticeDay(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}
